/** 控制台侧栏模块默认配置，与 Layout 导航项对齐 */
export interface SidebarModuleConfig {
  enabled?: boolean
  [key: string]: unknown
}

export interface SidebarModules {
  console?: SidebarModuleConfig & { items?: Record<string, SidebarModuleConfig> }
  personal?: SidebarModuleConfig & { items?: Record<string, SidebarModuleConfig> }
  admin?: SidebarModuleConfig & { items?: Record<string, SidebarModuleConfig> }
  [key: string]: unknown
}

export const DEFAULT_SIDEBAR_MODULES: SidebarModules = {
  console: {
    enabled: true,
    items: {
      detail: { enabled: true },
      token: { enabled: true },
      availableModels: { enabled: true },
      log: { enabled: true },
      playground: { enabled: true },
      midjourney: { enabled: false },
      task: { enabled: true },
    },
  },
  personal: {
    enabled: true,
    items: {
      personal: { enabled: true },
      topup: { enabled: true },
    },
  },
  admin: {
    enabled: true,
    items: {
      channel: { enabled: true },
      user: { enabled: true },
      model: { enabled: true },
      deployment: { enabled: true },
      redemption: { enabled: true },
      topupOrders: { enabled: true },
      subscription: { enabled: true },
      setting: { enabled: true },
    },
  },
}

export const SIDEBAR_SECTION_LABELS: Record<string, string> = {
  console: '控制台',
  personal: '个人',
  admin: '管理',
}

export const SIDEBAR_ITEM_LABELS: Record<string, Record<string, string>> = {
  console: {
    detail: '仪表盘',
    token: 'API Keys',
    availableModels: '可用模型',
    log: '日志',
    playground: 'Playground',
    midjourney: 'Midjourney',
    task: '异步任务',
  },
  personal: {
    personal: '个人设置',
    topup: '充值',
  },
  admin: {
    channel: '渠道',
    user: '用户',
    model: '模型管理',
    deployment: '模型部署',
    redemption: '兑换码',
    topupOrders: '充值订单',
    subscription: '订阅',
    setting: '系统设置',
  },
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

function parseSidebarModulesRaw(raw: unknown): SidebarModules | null {
  if (!raw) return null
  let parsed: unknown = raw
  if (typeof raw === 'string') {
    if (!raw.trim()) return null
    try {
      parsed = JSON.parse(raw)
    } catch {
      return null
    }
  }
  if (!isRecord(parsed)) return null
  return parsed as SidebarModules
}

/** 解析用户侧栏覆盖项，无效时返回空对象 */
export function parseUserSidebarModules(raw: unknown): SidebarModules {
  return parseSidebarModulesRaw(raw) ?? {}
}

/** 解析 SidebarModulesAdmin，结构异常时回落默认配置 */
export function parseSidebarModules(raw: unknown): SidebarModules {
  const parsed = parseSidebarModulesRaw(raw)
  if (!parsed) return structuredClone(DEFAULT_SIDEBAR_MODULES)

  const merged = structuredClone(DEFAULT_SIDEBAR_MODULES)
  for (const section of Object.keys(SIDEBAR_SECTION_LABELS)) {
    const src = parsed[section]
    if (!isRecord(src)) continue
    const dst = merged[section] as SidebarModuleConfig & { items?: Record<string, SidebarModuleConfig> }
    if (typeof src.enabled === 'boolean') dst.enabled = src.enabled
    const srcItems = src.items
    if (!isRecord(srcItems)) continue
    dst.items = dst.items ?? {}
    for (const itemKey of Object.keys(dst.items)) {
      const itemSrc = srcItems[itemKey]
      if (!isRecord(itemSrc)) continue
      if (typeof itemSrc.enabled === 'boolean') {
        dst.items[itemKey] = { ...dst.items[itemKey], enabled: itemSrc.enabled }
      }
    }
  }
  return merged
}

export function serializeSidebarModules(modules: SidebarModules): string {
  return JSON.stringify(modules, null, 2)
}
